import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Calendar, BookOpen, Users, Eye, Workflow } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";

import JoursFeriesBibliotheque from "./JoursFeriesBibliotheque";
import JoursFeriesCalendriers from "./JoursFeriesCalendriers";
import JoursFeriesAffectations from "./JoursFeriesAffectations";
import MesJoursFeries from "./MesJoursFeries";

// Utility function for class names
function cn(...inputs) {
  return inputs.filter(Boolean).join(" ");
}

const JoursFeries = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "mes-jours-feries";
  const setActiveTab = (tab) => setSearchParams({ tab }, { replace: true });

  const tabs = [
    {
      id: "bibliotheque",
      label: t("holidayHub.tabs.library.label"),
      icon: BookOpen,
      description: t("holidayHub.tabs.library.description"),
      section: "configuration",
      step: 1,
    },
    {
      id: "calendriers",
      label: t("holidayHub.tabs.calendars.label"),
      icon: Calendar,
      description: t("holidayHub.tabs.calendars.description"),
      section: "configuration",
      step: 1,
    },

    {
      id: "affectations",
      label: t("holidayHub.tabs.assignments.label"),
      icon: Users,
      description: t("holidayHub.tabs.assignments.description"),
      section: "assignment",
      step: 2,
    },

    {
      id: "mes-jours-feries",
      label: t("holidayHub.tabs.myHolidays.label"),
      icon: Eye,
      description: t("holidayHub.tabs.myHolidays.description"),
      section: "consultation",
      step: 3,
    },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case "bibliotheque":
        return <JoursFeriesBibliotheque />;
      case "calendriers":
        return <JoursFeriesCalendriers />;
      case "affectations":
        return <JoursFeriesAffectations />;
      case "mes-jours-feries":
        return <MesJoursFeries />;
      default:
        return <MesJoursFeries />;
    }
  };

  // Get current tab info
  const currentTab = tabs.find(t => t.id === activeTab);

  // Section colors - dark-safe tokens
  const sectionColors = {
    configuration: {
      bg: "bg-primary/10",
      border: "border-primary/40",
      borderLeft: "border-l-primary",
      text: "text-primary",
      buttonActive: "bg-primary text-white border-primary",
      buttonHover: "hover:bg-primary/10 hover:border-primary/40 hover:text-primary",
      badgeBg: "bg-primary/20",
      badgeText: "text-primary",
    },
    assignment: {
      bg: "bg-muted",
      border: "border-border",
      borderLeft: "border-l-muted-foreground",
      text: "text-muted-foreground",
      buttonActive: "bg-secondary text-secondary-foreground border-border",
      buttonHover: "hover:bg-accent hover:border-border hover:text-foreground",
      badgeBg: "bg-muted",
      badgeText: "text-muted-foreground",
    },
    consultation: {
      bg: "bg-green-950/20",
      border: "border-green-900/40",
      borderLeft: "border-l-green-500",
      text: "text-green-400",
      buttonActive: "bg-green-600 text-white border-green-600",
      buttonHover: "hover:bg-green-950/20 hover:border-green-900/40 hover:text-green-400",
      badgeBg: "bg-green-950/30",
      badgeText: "text-green-400",
    },
  };

  // Get tabs grouped by section
  const sections = [
    { id: "configuration", name: t("holidayHub.sections.configuration"), step: 1, tabs: tabs.filter(t => t.section === "configuration") },
    { id: "assignment", name: t("holidayHub.sections.assignment"), step: 2, tabs: tabs.filter(t => t.section === "assignment") },
    { id: "consultation", name: t("holidayHub.sections.consultation"), step: 3, tabs: tabs.filter(t => t.section === "consultation") },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold leading-7 sm:text-3xl">
            {t("holidayHub.title")}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("holidayHub.subtitle")}
          </p>
        </div>

        <Badge variant="outline" className="flex items-center gap-1.5 px-3 py-1.5">
          <Workflow className="h-3.5 w-3.5" />
          <span className="font-medium">{t("holidayHub.badge")}</span>
        </Badge>
      </div>

      {/* Main Content Card - Untitled UI Style */}
      <Card className="shadow-sm">
        <CardContent className="p-8">
          {/* Navigation Title */}
          <div className="mb-6">
            <h3 className="text-base font-semibold flex items-center gap-2">
              <Workflow className="h-5 w-5 text-muted-foreground" />
              {t('planning.nav.quickNav')}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {t("holidayHub.quickNavSubtitle")}
            </p>
          </div>

          {/* Navigation Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {sections.map((section) => {
              const colors = sectionColors[section.id];
              const sectionTabs = section.tabs;
              const hasActiveTab = sectionTabs.some(t => t.id === activeTab);

              return (
                <Card
                  key={section.name}
                  className={cn(
                    "border-2 transition-all hover:shadow-md bg-card",
                    hasActiveTab
                      ? cn(colors.borderLeft, "border-l-4 shadow-md", colors.bg)
                      : "border-border hover:border-muted-foreground/30"
                  )}
                >
                  <CardHeader className="p-5 pb-4 space-y-4">
                    {/* Section Header */}
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-sm">{section.name}</h4>
                      <Badge
                        variant="outline"
                        className={cn(
                          "text-xs font-medium",
                          hasActiveTab ? cn(colors.badgeBg, colors.badgeText, "border-transparent") : "bg-muted text-muted-foreground"
                        )}
                      >
                        {t('planning.nav.step', { n: section.step })}
                      </Badge>
                    </div>

                    <Separator />

                    {/* Action Buttons */}
                    <div className="space-y-2">
                      {sectionTabs.map((tab) => {
                        const Icon = tab.icon;
                        const isActive = activeTab === tab.id;

                        return (
                          <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={cn(
                              "w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg border-2 transition-all text-left font-medium text-sm",
                              isActive
                                ? colors.buttonActive
                                : cn("bg-card border-border text-foreground", colors.buttonHover)
                            )}
                          >
                            <Icon className="h-4 w-4 flex-shrink-0" />
                            <span className="flex-1">{tab.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </CardHeader>
                </Card>
              );
            })}
          </div>

          <Separator className="my-8" />

          {/* Current Section Context Banner */}
          {currentTab && (
            <Card className={cn(
              "border-2 mb-6",
              sectionColors[currentTab.section].bg,
              sectionColors[currentTab.section].border
            )}>
              <CardHeader className="py-4 px-5">
                <div className="flex items-center gap-4">
                  <div className={cn(
                    "p-3 rounded-xl shadow-sm",
                    sectionColors[currentTab.section].buttonActive.split(' ')[0],
                    "text-white"
                  )}>
                    {(() => {
                      const Icon = currentTab.icon;
                      return <Icon className="h-5 w-5" />;
                    })()}
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-base font-semibold flex items-center gap-2 mb-1">
                      {currentTab.label}
                      <Badge variant="outline" className={cn(
                        "text-xs font-medium",
                        sectionColors[currentTab.section].badgeBg,
                        sectionColors[currentTab.section].badgeText,
                        "border-transparent"
                      )}>
                        {t('planning.nav.step', { n: currentTab.step })}
                      </Badge>
                    </CardTitle>
                    <CardDescription className={cn("text-sm", sectionColors[currentTab.section].text)}>
                      {currentTab.description}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
            </Card>
          )}

          {/* Tab Content */}
          <div className="min-h-[500px]">
            {renderTabContent()}
          </div>
        </CardContent>
      </Card>

      {/* Info Footer */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl" style={{ backgroundColor: "rgba(94,106,210,0.12)" }}>
              <Calendar className="h-6 w-6" style={{ color: "#5e6ad2" }} />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-foreground text-base mb-2">
                {t("holidayHub.footer.title")}
              </h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t("holidayHub.footer.description")}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
                <div className="flex items-center gap-2.5 text-sm text-foreground font-medium">
                  <div className="p-1.5 bg-muted rounded-lg">
                    <BookOpen className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <span>{t("holidayHub.footer.library")}</span>
                </div>
                <div className="flex items-center gap-2.5 text-sm text-foreground font-medium">
                  <div className="p-1.5 bg-muted rounded-lg">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <span>{t("holidayHub.footer.calendars")}</span>
                </div>
                <div className="flex items-center gap-2.5 text-sm text-foreground font-medium">
                  <div className="p-1.5 bg-muted rounded-lg">
                    <Users className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <span>{t("holidayHub.footer.assignment")}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default JoursFeries;
